import { Component } from '@theme/component';

/** URL parameter the welcome email's discount link redirects back with. */
const CODE_PARAM = 'offer_code';
/** Code from the email link, kept for the browsing session so the bar keeps saying it's applied. */
const CODE_STORAGE_KEY = 'mint-offer-code';
/** Set once someone has signed up (or arrived from the email), so the bar stops offering the code. */
const SIGNED_UP_STORAGE_KEY = 'mint-offer-signed-up';

const KLAVIYO_REVISION = '2026-07-15';
/** Klaviyo metric recorded on every sign-up, including repeats, with the form's `source`. */
const SUBMITTED_METRIC = 'Submitted Signup Offer Form';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Secondary announcement bar + first-order discount popup.
 *
 * - The bar's button opens the signup form, which subscribes the email to a Klaviyo list.
 *   Klaviyo's welcome flow then emails a unique code and a `/discount/CODE` link.
 * - That link redirects back here with `?offer_code=CODE`: the bar switches to "code applied"
 *   and the popup opens on a welcome view showing the code.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dialog
 * @property {HTMLFormElement} [form]
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [website] - Honeypot, hidden from people.
 * @property {HTMLInputElement} [consent]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [successEmail]
 * @property {HTMLElement[]} [appliedCode]
 * @property {HTMLButtonElement} [copyButton]
 *
 * @extends {Component<Refs>}
 */
class SignupOfferComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    const params = new URLSearchParams(window.location.search);
    const codeFromUrl = params.get(CODE_PARAM)?.trim();

    if (codeFromUrl) {
      storage.set(sessionStorage, CODE_STORAGE_KEY, codeFromUrl);
      storage.set(localStorage, SIGNED_UP_STORAGE_KEY, '1');

      params.delete(CODE_PARAM);
      const query = params.toString();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
      history.replaceState(history.state, '', url);
    }

    if (storage.get(localStorage, SIGNED_UP_STORAGE_KEY)) this.dataset.signedUp = '';

    const code = codeFromUrl || storage.get(sessionStorage, CODE_STORAGE_KEY);
    if (code) this.#showApplied(code);
    if (codeFromUrl) this.#openView('welcome');

    this.refs.dialog.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('keydown', this.#handleKeydown);
  }

  open() {
    this.#openView('form');
    this.refs.email?.focus();
  }

  /** Reopens the welcome view from the bar's "code applied" message. */
  showCode() {
    this.#openView('welcome');
  }

  close() {
    if (this.refs.dialog.matches(':popover-open')) this.refs.dialog.hidePopover();
  }

  updateSubmitState() {
    const { consent, submit } = this.refs;
    if (consent && submit) submit.disabled = !consent.checked;
  }

  /** @param {SubmitEvent} event */
  async subscribe(event) {
    event.preventDefault();

    const address = await signUp(this, this.refs);
    if (address === undefined) return;

    this.dataset.signedUp = '';
    this.#showSuccess(address);
  }

  async copyCode() {
    const code = storage.get(sessionStorage, CODE_STORAGE_KEY);
    const { copyButton } = this.refs;
    if (!code || !copyButton) return;

    try {
      await navigator.clipboard.writeText(code);
      copyButton.dataset.copied = '';
      setTimeout(() => delete copyButton.dataset.copied, 2000);
    } catch {
      // Clipboard blocked; the code is still visible to copy by hand.
    }
  }

  /** @param {string} address */
  #showSuccess(address) {
    if (this.refs.successEmail) this.refs.successEmail.textContent = address;
    this.refs.dialog.dataset.view = 'success';
    this.refs.dialog.querySelector('[data-view-panel="success"] button')?.focus();
  }

  /** @param {string} code */
  #showApplied(code) {
    this.dataset.applied = '';
    for (const element of this.refs.appliedCode ?? []) element.textContent = code;
  }

  /** @param {'form' | 'success' | 'welcome'} view */
  #openView(view) {
    const { dialog } = this.refs;
    dialog.dataset.view = view;
    if (!dialog.matches(':popover-open')) dialog.showPopover();
  }

  /** Manual popovers don't close on Escape by themselves. */
  #handleKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Escape') this.close();
  };
}

/**
 * Inline version of the same first-order offer (the footer banner). It signs up to the same
 * Klaviyo list as the popup, so it triggers the same code email, then swaps the form for a
 * "check your inbox" message.
 *
 * @typedef {SignupFormRefs & { form: HTMLFormElement, success: HTMLElement }} SignupFormComponentRefs
 *
 * @extends {Component<SignupFormComponentRefs>}
 */
class SignupFormComponent extends Component {
  requiredRefs = ['form', 'success'];

  /** @param {SubmitEvent} event */
  async subscribe(event) {
    event.preventDefault();

    const address = await signUp(this, this.refs);
    if (address === undefined) return;

    const { form, success, successEmail } = this.refs;
    if (successEmail) successEmail.textContent = address;
    form.hidden = true;
    success.hidden = false;
    success.focus();
  }
}

/**
 * @typedef {object} SignupFormRefs
 * @property {HTMLInputElement} [email]
 * @property {HTMLInputElement} [website] - Honeypot, hidden from people.
 * @property {HTMLInputElement} [consent]
 * @property {HTMLButtonElement} [submit]
 * @property {HTMLElement} [status]
 * @property {HTMLElement} [successEmail]
 */

/**
 * Validates a signup form and subscribes its email to the Klaviyo list, which triggers the
 * welcome flow that emails the unique code. Problems are shown in the form's status line.
 *
 * `data-source` on the component is saved with the subscription (Klaviyo shows it as the
 * "custom method detail"), which is how sign-ups from the popup and the footer banner are told apart.
 *
 * @param {HTMLElement} component - Carries `data-klaviyo-public-key`, `data-klaviyo-list-id` and `data-source`.
 * @param {SignupFormRefs} refs
 * @returns {Promise<string | undefined>} The email address, once it's signed up.
 */
async function signUp(component, refs) {
  const { email, website, consent, submit, status } = refs;
  if (!email || !submit) return;

  /** @param {string} message */
  const setStatus = (message) => {
    if (status) status.textContent = message;
  };

  const address = email.value.trim();

  // Bots fill in every field; people never see this one. Pretend it worked.
  if (website?.value) return address;

  if (!EMAIL_PATTERN.test(address)) {
    setStatus('Please enter a valid email address.');
    return;
  }

  if (consent && !consent.checked) {
    setStatus('Please tick the box to continue.');
    return;
  }

  const { klaviyoPublicKey, klaviyoListId, source } = component.dataset;
  if (!klaviyoPublicKey || !klaviyoListId) {
    setStatus('Sign-up isn’t available right now. Please try again later.');
    return;
  }

  submit.disabled = true;
  setStatus('');

  try {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(klaviyoPublicKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: source,
              // The client endpoint itself records email marketing consent; it rejects
              // a `subscriptions` field on the profile.
              profile: { data: { type: 'profile', attributes: { email: address } } },
            },
            relationships: { list: { data: { type: 'list', id: klaviyoListId } } },
          },
        }),
      }
    );

    if (!response.ok) throw new Error(`Klaviyo responded ${response.status}: ${await response.text()}`);

    recordSubmission(klaviyoPublicKey, { email: address, listId: klaviyoListId, source });
    storage.set(localStorage, SIGNED_UP_STORAGE_KEY, '1');
    return address;
  } catch (error) {
    console.error(error);
    setStatus('Something went wrong. Please try again.');
  } finally {
    submit.disabled = Boolean(consent && !consent.checked);
  }
}

/**
 * Records every sign-up as a Klaviyo event tagged with the form's source. Klaviyo only notes the
 * source when someone newly subscribes, so a sign-up from someone already subscribed, or already
 * on the list, would otherwise leave no trace of which form they used. The sign-up has already
 * succeeded, so a failure here is only logged.
 *
 * @param {string} publicKey
 * @param {{ email: string, listId: string, source: string | undefined }} details
 */
function recordSubmission(publicKey, { email, listId, source }) {
  fetch(`https://a.klaviyo.com/client/events?company_id=${encodeURIComponent(publicKey)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/vnd.api+json',
      revision: KLAVIYO_REVISION,
    },
    body: JSON.stringify({
      data: {
        type: 'event',
        attributes: {
          properties: { list_id: listId, source },
          metric: { data: { type: 'metric', attributes: { name: SUBMITTED_METRIC } } },
          profile: { data: { type: 'profile', attributes: { email } } },
        },
      },
    }),
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`Klaviyo responded ${response.status}: ${await response.text()}`);
    })
    .catch(console.error);
}

/** Storage can throw (private mode, blocked site data) — treat that as empty. */
const storage = {
  /** @param {Storage} store @param {string} key */
  get(store, key) {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  },
  /** @param {Storage} store @param {string} key @param {string} value */
  set(store, key, value) {
    try {
      store.setItem(key, value);
    } catch {}
  },
};

if (!customElements.get('signup-offer-component')) {
  customElements.define('signup-offer-component', SignupOfferComponent);
}

if (!customElements.get('signup-form-component')) {
  customElements.define('signup-form-component', SignupFormComponent);
}
